import React, { useState, useMemo, useCallback, useEffect } from 'react';

// Clave de localStorage
const LOCAL_STORAGE_KEY = 'inventarioMedicamentosFarmaceutico';

// =========================================================================
// INLINE SVG ICONS (Usando Lucide-React-style definitions)
// =========================================================================

const IconWrapper = ({ children, className, ...props }) => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="24" 
        height="24" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        className={className} 
        {...props}
    >
        {children}
    </svg>
);

const PlusIcon = (props) => (
    <IconWrapper {...props}><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></IconWrapper>
);

const SearchIcon = (props) => (
    <IconWrapper {...props}><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></IconWrapper>
);

const XIcon = (props) => (
    <IconWrapper {...props}><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></IconWrapper>
);

const EditIcon = (props) => (
    <IconWrapper {...props}><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></IconWrapper>
);

const TrashIcon = (props) => (
    <IconWrapper {...props}>
        <path d="M3 6h18"></path>
        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
    </IconWrapper>
);

const AlertTriangleIcon = (props) => (
    <IconWrapper {...props}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></IconWrapper>
);

const PackageIcon = (props) => (
    <IconWrapper {...props}><path d="M12.89 1.45l6.58 3.59a2 2 0 0 1 1.05 1.83v11.26a2 2 0 0 1-1.05 1.83l-6.58 3.59a2 2 0 0 1-1.89 0l-6.58-3.59a2 2 0 0 1-1.05-1.83V6.87a2 2 0 0 1 1.05-1.83z"></path><path d="M22 6.87l-9.17 5L2 6.87"></path><path d="M12.83 22.5l-.54-10.43"></path></IconWrapper>
);

const WarehouseIcon = (props) => (
    <IconWrapper {...props}><path d="M22 20h-4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2z"></path><path d="M2 14h6"></path><path d="M2 10h6"></path><path d="M16 20v-8a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v8"></path><path d="M16 4h-4"></path></IconWrapper>
);

const CalendarIcon = (props) => (
    <IconWrapper {...props}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></IconWrapper>
);

// CORRECCIÓN: Se aseguró la etiqueta de cierre para la definición SVG.
const DropletIcon = (props) => (
    <IconWrapper {...props}><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.32 0l5.66-5.66z"></path></IconWrapper>
);


// Simulación de datos iniciales (Solo se usa si no hay datos guardados en localStorage)
const initialMedicamentos = [
    { id: 1, nombre: 'Amoxicilina 500mg', codigo: 'AMX001', stock: 150, unidad: 'Cápsulas', umbral: 50, proveedor: 'PharmaCorp', fechaExpiracion: '2025-10-01', ubicacion: 'Estante A3' },
    { id: 2, nombre: 'Ibuprofeno 400mg', codigo: 'IBU002', stock: 25, unidad: 'Tabletas', umbral: 30, proveedor: 'MedDistrib', fechaExpiracion: '2026-03-15', ubicacion: 'Estante B1' },
    { id: 3, nombre: 'Paracetamol 500mg', codigo: 'PCM003', stock: 300, unidad: 'Tabletas', umbral: 100, proveedor: 'PharmaCorp', fechaExpiracion: '2024-12-20', ubicacion: 'Estante A1' },
    { id: 4, nombre: 'Insulina Glargina', codigo: 'INS004', stock: 15, unidad: 'Viales', umbral: 20, proveedor: 'BioTech', fechaExpiracion: '2024-03-01', ubicacion: 'Refrigerador C' }, // Vencido
    { id: 5, nombre: 'Salbutamol Spray', codigo: 'SBT005', stock: 80, unidad: 'Inhalador', umbral: 40, proveedor: 'AeroMed', fechaExpiracion: '2026-11-10', ubicacion: 'Estante D2' },
];

/**
 * Función utilitaria para determinar el estado del stock (color y mensaje).
 */
const getStockStatus = (stock, umbral, expiryDate) => {
    const today = new Date();
    // Normalizar la fecha de expiración a medianoche del día para una comparación justa
    const expiry = new Date(expiryDate + 'T00:00:00'); 
    const daysToExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

    if (daysToExpiry < 0) {
        return {
            label: 'VENCIDO',
            color: 'bg-red-700 text-white border-red-900',
            icon: <CalendarIcon className="w-4 h-4 mr-1" />,
            sortOrder: 1, // El más crítico
        };
    }
    if (stock < umbral) {
        return {
            label: 'STOCK CRÍTICO',
            color: 'bg-red-100 text-red-800 border-red-500',
            icon: <AlertTriangleIcon className="w-4 h-4 mr-1" />,
            sortOrder: 2,
        };
    }
    if (daysToExpiry <= 60) { // Alerta de expiración crítica (2 meses o menos)
         return {
            label: 'EXPIRA PRONTO',
            color: 'bg-yellow-100 text-yellow-800 border-yellow-500',
            icon: <CalendarIcon className="w-4 h-4 mr-1" />,
            sortOrder: 3,
        };
    }
    
    return {
        label: 'NORMAL',
        color: 'bg-green-100 text-green-800 border-green-500',
        icon: <PackageIcon className="w-4 h-4 mr-1" />,
        sortOrder: 4, // El menos crítico
    };
};


/**
 * Componente Modal para agregar o editar un medicamento.
 */
const MedicamentoModal = ({ isOpen, onClose, medicamento, onSave }) => {
    const defaultState = {
        nombre: '', codigo: '', stock: 0, unidad: '', umbral: 0, proveedor: '', fechaExpiracion: '', ubicacion: ''
    };
    const [formData, setFormData] = useState(medicamento || defaultState);

    // Sincroniza el formulario si el medicamento de edición cambia
    useEffect(() => {
        setFormData(medicamento || defaultState);
    }, [medicamento, isOpen]); // Añadir isOpen asegura reseteo al abrir/cerrar

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            // Convertir a número solo si el tipo es number, asegurando que no sea NaN
            [name]: type === 'number' ? (value === '' ? '' : parseInt(value) || 0) : value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Validar que stock y umbral no sean cadenas vacías si son numéricos
        const stockValue = formData.stock === '' ? 0 : formData.stock;
        const umbralValue = formData.umbral === '' ? 0 : formData.umbral;

        onSave({ ...formData, stock: stockValue, umbral: umbralValue });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl transform transition-all duration-300">
                <div className="p-6 border-b flex justify-between items-center bg-indigo-50 rounded-t-xl">
                    <h3 className="text-2xl font-bold text-indigo-700">
                        {medicamento ? '📝 Editar Medicamento' : '✨ Agregar Nuevo Medicamento'}
                    </h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-900 transition">
                        <XIcon className="w-6 h-6" />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    
                    {/* Fila 1: Nombre y Código */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <ModalInput label="Nombre del Medicamento" name="nombre" value={formData.nombre} onChange={handleChange} required type="text" icon={DropletIcon} />
                        <ModalInput label="Código SKU/Lote" name="codigo" value={formData.codigo} onChange={handleChange} required type="text" icon={PackageIcon} />
                    </div>

                    {/* Fila 2: Stock, Unidad y Umbral */}
                    <div className="grid grid-cols-3 gap-4">
                        <ModalInput label="Stock Actual" name="stock" value={formData.stock} onChange={handleChange} required type="number" min="0" />
                        <ModalInput label="Unidad de Medida" name="unidad" value={formData.unidad} onChange={handleChange} required type="text" placeholder="Ej: Cápsulas, Viales" />
                        <ModalInput label="Umbral Mínimo" name="umbral" value={formData.umbral} onChange={handleChange} required type="number" min="0" />
                    </div>

                    {/* Fila 3: Proveedor, Ubicación y Expiración */}
                    <div className="grid grid-cols-3 gap-4">
                        <ModalInput label="Proveedor" name="proveedor" value={formData.proveedor} onChange={handleChange} type="text" icon={WarehouseIcon} />
                        <ModalInput label="Ubicación de Almacén" name="ubicacion" value={formData.ubicacion} onChange={handleChange} type="text" placeholder="Ej: Refrigerador A, Estante 4" />
                        <ModalInput label="Fecha de Expiración" name="fechaExpiracion" value={formData.fechaExpiracion} onChange={handleChange} required type="date" icon={CalendarIcon} />
                    </div>

                    <div className="flex justify-end pt-4">
                        <button
                            type="submit"
                            className="flex items-center px-8 py-3 bg-indigo-600 text-white font-bold rounded-xl shadow-xl hover:bg-indigo-700 transition duration-200 transform hover:scale-105 active:scale-95"
                        >
                            <span className='mr-2'>{medicamento ? 'Guardar Cambios' : 'Crear Medicamento'}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Helper Component for Modal Inputs
const ModalInput = ({ label, name, value, onChange, type = 'text', required = false, min, icon: Icon, placeholder }) => (
    <div>
        <label className="block text-sm font-bold text-gray-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>
        <div className="relative">
            {Icon && <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-indigo-400" />}
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                min={min}
                placeholder={placeholder}
                className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 ${Icon ? 'pl-10' : ''}`}
            />
        </div>
    </div>
);


/**
 * Componente principal de Gestión de Medicamentos.
 */
const Medicamentos = () => {
    
    // Función de inicialización que carga los datos de localStorage o usa los iniciales
    const loadInitialState = () => {
        // 
        try {
            const storedData = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (storedData) {
                // Si hay datos guardados, los parseamos y retornamos
                return JSON.parse(storedData);
            }
        } catch (error) {
            console.error("Error al cargar el estado de localStorage. Usando datos iniciales.", error);
        }
        // Si no hay datos o falla la carga, retorna los datos de simulación
        return initialMedicamentos;
    };

    // 1. Inicialización del estado
    const [medicamentos, setMedicamentos] = useState(loadInitialState);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingMedicamento, setEditingMedicamento] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // 2. Efecto para guardar datos en localStorage
    useEffect(() => {
        try {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(medicamentos));
        } catch (error) {
            // Manejo de errores si localStorage está lleno o deshabilitado
            console.error("Error al guardar el estado en localStorage.", error);
        }
    }, [medicamentos]); 

    // Lógica CRUD
    const handleSave = useCallback((newMedicamento) => {
        setMedicamentos(prevMedicamentos => {
            if (newMedicamento.id) {
                // Edición
                return prevMedicamentos.map(m =>
                    m.id === newMedicamento.id ? newMedicamento : m
                );
            } else {
                // Creación (Usamos Date.now() para un ID único simple)
                const id = Date.now(); 
                return [...prevMedicamentos, { ...newMedicamento, id }];
            }
        });
    }, []);

    const handleEdit = useCallback((medicamento) => {
        setEditingMedicamento(medicamento);
        setIsModalOpen(true);
    }, []);

    const handleDelete = useCallback((id) => {
        // Uso de prompt() en lugar de window.confirm() para compatibilidad en entornos iframes
        const isConfirmed = prompt('Escribe "ELIMINAR" para confirmar la eliminación de este medicamento:');
        if (isConfirmed === 'ELIMINAR') {
            setMedicamentos(medicamentos.filter(m => m.id !== id));
        } else {
            console.log("Eliminación cancelada.");
        }
    }, [medicamentos]);

    const handleOpenCreate = useCallback(() => {
        setEditingMedicamento(null);
        setIsModalOpen(true);
    }, []);

    // Filtro y Búsqueda + Ordenamiento por Estado de Riesgo
    const filteredAndSortedMedicamentos = useMemo(() => {
        let filtered = medicamentos;
        if (searchTerm) {
            const lowerCaseSearch = searchTerm.toLowerCase();
            filtered = medicamentos.filter(med =>
                med.nombre.toLowerCase().includes(lowerCaseSearch) ||
                med.codigo.toLowerCase().includes(lowerCaseSearch) ||
                med.proveedor.toLowerCase().includes(lowerCaseSearch) ||
                med.ubicacion.toLowerCase().includes(lowerCaseSearch)
            );
        }

        // Ordenar: Los estados más críticos (sortOrder más bajo) van primero
        return filtered.sort((a, b) => {
            const statusA = getStockStatus(a.stock, a.umbral, a.fechaExpiracion).sortOrder;
            const statusB = getStockStatus(b.stock, b.umbral, b.fechaExpiracion).sortOrder;
            
            // Si el riesgo es el mismo, ordenar por nombre alfabético
            if (statusA === statusB) {
                return a.nombre.localeCompare(b.nombre);
            }

            return statusA - statusB;
        });

    }, [medicamentos, searchTerm]);

    // Calcular métricas para tarjetas de resumen
    const metrics = useMemo(() => {
        let criticoCount = 0;
        let expiraProntoCount = 0;
        let vencidoCount = 0;

        medicamentos.forEach(med => {
            const status = getStockStatus(med.stock, med.umbral, med.fechaExpiracion);
            if (status.label === 'STOCK CRÍTICO') criticoCount++;
            if (status.label === 'EXPIRA PRONTO') expiraProntoCount++;
            if (status.label === 'VENCIDO') vencidoCount++;
        });

        return { 
            totalMedicamentos: medicamentos.length,
            criticoCount, 
            expiraProntoCount, 
            vencidoCount 
        };
    }, [medicamentos]);


    return (
        <div className="p-4 sm:p-8 bg-gray-50 min-h-screen font-sans">
            <h1 className="text-4xl font-extrabold text-indigo-800 border-b-4 border-indigo-200 pb-3 mb-8">
                <span className='mr-3'>💊</span> Gestión de Inventario Farmacéutico
            </h1>
            
            

            {/* Panel de Control: Búsqueda y Botón Agregar */}
            <div className="flex flex-col sm:flex-row justify-between items-center bg-white p-5 rounded-xl shadow-lg mb-8 border-t-4 border-indigo-500">
                
                {/* Búsqueda */}
                <div className="relative w-full sm:w-2/5 mb-4 sm:mb-0">
                    <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Buscar por nombre, código o ubicación..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-indigo-600 focus:border-indigo-600 transition duration-150 shadow-inner"
                    />
                </div>

                {/* Botón Agregar */}
                <button
                    onClick={handleOpenCreate}
                    className="flex items-center px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl shadow-lg hover:bg-indigo-700 transition duration-200 transform hover:scale-[1.02] active:scale-95"
                >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Nuevo Medicamento
                </button>
            </div>

            {/* Tarjetas de Resumen de Riesgo */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                <MetricCard 
                    title="Total SKU Únicos" 
                    value={metrics.totalMedicamentos} 
                    icon={PackageIcon} 
                    color="indigo" 
                />
                <MetricCard 
                    title="Stock Crítico" 
                    value={metrics.criticoCount} 
                    icon={AlertTriangleIcon} 
                    color="red" 
                    glow={metrics.criticoCount > 0} 
                />
                <MetricCard 
                    title="Próximo a Expirar (60 días)" 
                    value={metrics.expiraProntoCount} 
                    icon={CalendarIcon} 
                    color="yellow" 
                    glow={metrics.expiraProntoCount > 0} 
                />
                <MetricCard 
                    title="Medicamentos Vencidos" 
                    value={metrics.vencidoCount} 
                    icon={CalendarIcon} 
                    color="gray" 
                    glow={metrics.vencidoCount > 0} 
                />
            </div>


            {/* Tabla de Listado de Medicamentos */}
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden border">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-indigo-50">
                            <tr>
                                <TableHeader title="Medicamento / Código" />
                                <TableHeader title="Ubicación" />
                                <TableHeader title="Stock Actual" />
                                <TableHeader title="Umbral Mínimo" />
                                <TableHeader title="Fecha Expiración" />
                                <TableHeader title="Estado de Riesgo" />
                                <TableHeader title="Acciones" isRightAlign />
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {filteredAndSortedMedicamentos.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-10 text-center text-gray-500 text-lg italic">
                                        No hay resultados que coincidan con la búsqueda.
                                    </td>
                                </tr>
                            ) : (
                                filteredAndSortedMedicamentos.map((med) => {
                                    const status = getStockStatus(med.stock, med.umbral, med.fechaExpiracion);
                                    return (
                                        <tr key={med.id} className={`transition duration-150 ${status.sortOrder <= 2 ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-indigo-50'}`}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                                {med.nombre} <br />
                                                <span className="text-gray-500 font-normal text-xs">{med.codigo}</span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">
                                                {med.ubicacion}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                                                <span className={`font-bold ${status.sortOrder === 2 ? 'text-red-600' : ''}`}>{med.stock}</span> {med.unidad}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {med.umbral} {med.unidad}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">
                                                {med.fechaExpiracion}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span 
                                                    className={`inline-flex items-center px-3 py-1 text-xs font-bold rounded-full border shadow-sm ${status.color}`}
                                                >
                                                    {status.icon}
                                                    {status.label}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                                <button
                                                    onClick={() => handleEdit(med)}
                                                    className="text-indigo-600 hover:text-white p-2 rounded-full hover:bg-indigo-500 transition duration-150 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                    title="Editar"
                                                >
                                                    <EditIcon className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(med.id)}
                                                    className="text-red-600 hover:text-white p-2 rounded-full hover:bg-red-500 transition duration-150 focus:outline-none focus:ring-2 focus:ring-red-500"
                                                    title="Eliminar"
                                                >
                                                    <TrashIcon className="w-5 h-5" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal de CRUD */}
            <MedicamentoModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                medicamento={editingMedicamento}
                onSave={handleSave}
            />
            
        </div>
    );
};

// --- Componentes auxiliares para mejorar la lectura del código ---

const TableHeader = ({ title, isRightAlign = false }) => (
    <th className={`px-6 py-3 text-xs font-bold text-indigo-700 uppercase tracking-wider ${isRightAlign ? 'text-right' : 'text-left'}`}>
        {title}
    </th>
);

const MetricCard = ({ title, value, icon: Icon, color, glow = false }) => {
    const colorClasses = {
        indigo: 'border-indigo-500 text-indigo-700 bg-indigo-50',
        red: 'border-red-500 text-red-700 bg-red-50',
        yellow: 'border-yellow-500 text-yellow-700 bg-yellow-50',
        gray: 'border-gray-500 text-gray-700 bg-gray-100',
    };
    
    const iconColor = {
        indigo: 'text-indigo-500',
        red: 'text-red-500',
        yellow: 'text-yellow-500',
        gray: 'text-gray-500',
    };
    
    // Clase para el efecto de "brillo" (glow) en las métricas de riesgo
    const glowClass = glow ? 
        (color === 'red' ? 'ring-2 ring-red-300 shadow-red-200' : 'ring-2 ring-yellow-300 shadow-yellow-200') : 
        '';

    return (
        <div className={`p-5 rounded-xl shadow-lg border-l-4 ${colorClasses[color]} flex items-center justify-between transition transform hover:shadow-xl ${glowClass}`}>
            <div>
                <p className="text-sm font-medium text-gray-500">{title}</p>
                <p className="text-3xl font-extrabold mt-1">{value}</p>
            </div>
            {Icon && <Icon className={`w-8 h-8 opacity-75 ${iconColor[color]}`} />}
        </div>
    );
};

export default Medicamentos;